import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Languages, Sparkles, CheckCircle2, AlertCircle, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ENTITY_LABELS = {
  games: "Jogos",
  tiers: "Escalões",
  robots_name: "Robots (Nome)",
  robots_alcunha: "Robots (Alcunha)",
  eliminatorias: "Eliminatórias",
  brand_name: "Nome da Marca",
};

const LANG_META = {
  en: { flag: "🇬🇧", name: "English", color: "from-sky-500 to-blue-600" },
  es: { flag: "🇪🇸", name: "Español", color: "from-amber-500 to-orange-600" },
};

function pct(n, d) {
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

function CoverageBar({ value }) {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 90 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${v}%` }} />
    </div>
  );
}

function LangCard({ lang, totals }) {
  const meta = LANG_META[lang];
  // overall = sum of translated / sum of totals across all entities
  const total = Object.values(totals).reduce((a, e) => a + (e.total || 0), 0);
  const translated = Object.values(totals).reduce((a, e) => a + (e.translated?.[lang] || 0), 0);
  const overall = pct(translated, total);
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-5" data-testid={`i18n-lang-card-${lang}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{meta.flag}</span>
          <div>
            <div className="font-[Fredoka] text-xl font-bold text-slate-900">{meta.name}</div>
            <div className="text-xs text-slate-500">{translated} / {total} traduções</div>
          </div>
        </div>
        <div className={`text-3xl font-bold bg-gradient-to-br ${meta.color} text-transparent bg-clip-text`}>
          {overall}%
        </div>
      </div>
      <CoverageBar value={overall} />
    </div>
  );
}

function EntityRow({ entity, data, onBulkTranslate, busyKey }) {
  const label = ENTITY_LABELS[entity] || entity;
  return (
    <div className="border-b border-slate-100 last:border-b-0 py-3 grid grid-cols-12 items-center gap-3" data-testid={`i18n-row-${entity}`}>
      <div className="col-span-3 font-semibold text-slate-800">{label}</div>
      <div className="col-span-1 text-sm text-slate-500">{data.total}</div>
      {["en", "es"].map((lang) => {
        const n = data.translated?.[lang] || 0;
        const p = pct(n, data.total);
        const missing = (data.total || 0) - n;
        const isBusy = busyKey === `${entity}:${lang}`;
        return (
          <React.Fragment key={lang}>
            <div className="col-span-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-600">{LANG_META[lang].flag} {n}/{data.total}</span>
                <span className="font-bold text-slate-700">{p}%</span>
              </div>
              <CoverageBar value={p} />
            </div>
            <div className="col-span-1 flex justify-end">
              {missing > 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  disabled={!!busyKey}
                  onClick={() => onBulkTranslate(entity, lang, missing)}
                  data-testid={`i18n-bulk-${entity}-${lang}`}
                  title={`Auto-traduzir ${missing} item(s) em falta para ${lang.toUpperCase()}`}
                >
                  {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  {isBusy ? "" : `${missing}`}
                </Button>
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" title="Tudo traduzido" />
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function I18nDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [lastReport, setLastReport] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditReport, setAuditReport] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/i18n/coverage");
      setData(r.data);
    } catch (e) {
      toast.error("Falhou a carregar a cobertura: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const bulkTranslate = async (entityType, lang, missing) => {
    setBusyKey(`${entityType}:${lang}`);
    setLastReport(null);
    try {
      const r = await api.post("/admin/i18n/bulk-translate", {
        entity_type: entityType,
        target_lang: lang,
        max_items: Math.min(missing, 100),
      });
      const { translated, errored, items } = r.data;
      toast.success(`Traduzidos ${translated} item(s) para ${lang.toUpperCase()}${errored ? ` · ${errored} erro(s)` : ""}`);
      setLastReport({ entityType, lang, items });
      await reload();
    } catch (e) {
      toast.error("Auto-tradução falhou: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyKey(null);
    }
  };

  const runAudit = async () => {
    if (!window.confirm(
      "A IA vai rever TODAS as traduções EN/ES existentes e corrigir automaticamente as que estiverem erradas, em PT por traduzir ou com casing incorreto. Continuar?"
    )) return;
    setAuditing(true);
    setAuditReport(null);
    try {
      const r = await api.post("/admin/i18n/audit", { auto_apply: true, max_items: 300 }, { timeout: 180000 });
      const { reviewed, fixed, errored, items } = r.data;
      toast.success(`Auditoria concluída · ${reviewed} revistos · ${fixed} corrigidos${errored ? ` · ${errored} erros` : ""}`);
      setAuditReport(r.data);
      await reload();
    } catch (e) {
      toast.error("Auditoria falhou: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAuditing(false);
    }
  };

  const [staticAuditing, setStaticAuditing] = useState(false);
  const [staticReport, setStaticReport] = useState(null);

  const runStaticAudit = async () => {
    const apply = window.confirm(
      "Auditar i18n.js:\n\nOK = aplicar correções automaticamente no ficheiro\nCancelar = só listar inconsistências (read-only)"
    );
    setStaticAuditing(true);
    setStaticReport(null);
    try {
      const r = await api.post(`/admin/i18n/audit-static?apply=${apply ? "true" : "false"}`, {}, { timeout: 180000 });
      const { reviewed, issues, all_ok, applied } = r.data;
      if (all_ok) toast.success(`✨ ${reviewed} chaves i18n.js revistas — tudo consistente!`);
      else if (apply) toast.success(`${reviewed} chaves revistas · ${applied} correções aplicadas no ficheiro`);
      else toast.warning(`${reviewed} chaves revistas · ${issues.length} inconsistência(s) (não aplicadas)`);
      setStaticReport(r.data);
    } catch (e) {
      toast.error("Auditoria estática falhou: " + (e?.response?.data?.detail || e.message));
    } finally {
      setStaticAuditing(false);
    }
  };

  const totals = data?.entities || {};

  return (
    <div className="p-6 max-w-6xl mx-auto pb-24">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-[Fredoka] text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Languages className="w-8 h-8 text-violet-600" />
            Cobertura Multilingue
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            % de campos traduzidos em EN/ES por entidade. Botão{" "}
            <Sparkles className="inline w-3.5 h-3.5 text-amber-500" /> auto-traduz tudo o que falta com Claude Sonnet 4.5.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="i18n-reload">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Recarregar
        </Button>
      </div>

      {/* AI Audit banner */}
      <div className="mb-6 rounded-2xl border-2 border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 p-5 flex flex-wrap items-center gap-4" data-testid="i18n-audit-banner">
        <ShieldCheck className="w-8 h-8 text-violet-600 shrink-0" />
        <div className="flex-1 min-w-[280px]">
          <div className="font-[Fredoka] text-lg font-bold text-slate-900">Auditoria global com IA</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Revê TODAS as traduções EN/ES existentes com Claude Sonnet 4.5 e corrige automaticamente inconsistências
            (texto em PT, casing errado, traduções literais incorretas).
          </div>
        </div>
        <Button
          onClick={runAudit}
          disabled={auditing || loading}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="i18n-run-audit"
        >
          {auditing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          {auditing ? "A auditar…" : "Auditar e corrigir"}
        </Button>
      </div>

      {/* Static i18n.js audit banner */}
      <div className="mb-6 rounded-2xl border-2 border-sky-200 bg-gradient-to-r from-sky-50 to-cyan-50 p-5 flex flex-wrap items-center gap-4" data-testid="i18n-static-audit-banner">
        <Sparkles className="w-8 h-8 text-sky-600 shrink-0" />
        <div className="flex-1 min-w-[280px]">
          <div className="font-[Fredoka] text-lg font-bold text-slate-900">Auditar i18n.js (strings estáticas)</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Revê as ~200 chaves estáticas do ficheiro <code className="bg-white px-1 rounded border text-[10px]">/app/frontend/src/lib/i18n.js</code> e
            detecta inconsistências terminológicas, casing, gramática e remanescentes em PT. Read-only (mostra sugestões para o admin aplicar).
          </div>
        </div>
        <Button
          onClick={runStaticAudit}
          disabled={staticAuditing}
          variant="outline"
          className="border-sky-400 text-sky-700 hover:bg-sky-100"
          data-testid="i18n-run-static-audit"
        >
          {staticAuditing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {staticAuditing ? "A auditar…" : "Auditar i18n.js"}
        </Button>
      </div>

      {staticReport && (
        <div className="mb-6 rounded-2xl bg-white shadow-sm border border-sky-200 p-5" data-testid="i18n-static-report">
          <div className="flex items-center justify-between mb-3">
            <div className="font-[Fredoka] text-lg font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-600" />
              Resultado i18n.js
            </div>
            <div className="flex gap-3 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-bold">
                {staticReport.reviewed} chaves
              </span>
              {staticReport.all_ok ? (
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                  ✨ Tudo consistente
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-bold">
                  {staticReport.issues?.length || 0} inconsistência(s)
                </span>
              )}
            </div>
          </div>
          {!staticReport.all_ok && (
            <div className="space-y-1 max-h-96 overflow-y-auto text-sm">
              {(staticReport.issues || []).map((it, i) => (
                <div key={`${it.key}-${it.lang}-${i}`} className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 mb-0.5">
                      <code className="bg-slate-100 px-1 rounded">{it.key}</code> · {LANG_META[it.lang]?.flag} {it.lang?.toUpperCase()} · {it.issue}
                    </div>
                    <div className="text-slate-500 text-xs">
                      <span className="italic">PT: {it.pt}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="line-through text-rose-600/70">{it.current}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-semibold text-emerald-700">{it.fix}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-16 text-slate-500">A carregar cobertura…</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <LangCard lang="en" totals={totals} />
            <LangCard lang="es" totals={totals} />
          </div>

          <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-5">
            <div className="grid grid-cols-12 text-xs font-bold uppercase tracking-wider text-slate-500 pb-2 border-b border-slate-200">
              <div className="col-span-3">Entidade</div>
              <div className="col-span-1">Total</div>
              <div className="col-span-3">🇬🇧 EN</div>
              <div className="col-span-1"></div>
              <div className="col-span-3">🇪🇸 ES</div>
              <div className="col-span-1"></div>
            </div>
            {Object.entries(totals).map(([entity, d]) => (
              <EntityRow
                key={entity}
                entity={entity}
                data={d}
                onBulkTranslate={bulkTranslate}
                busyKey={busyKey}
              />
            ))}
          </div>

          {auditReport && (
            <div className="mt-6 rounded-2xl bg-white shadow-sm border border-violet-200 p-5" data-testid="i18n-audit-report">
              <div className="flex items-center justify-between mb-3">
                <div className="font-[Fredoka] text-lg font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-violet-600" />
                  Resultado da auditoria
                </div>
                <div className="flex gap-3 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-bold">
                    {auditReport.reviewed} revistos
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                    {auditReport.fixed} corrigidos
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 font-bold">
                    {auditReport.ok} OK
                  </span>
                  {!!auditReport.errored && (
                    <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 font-bold">
                      {auditReport.errored} erros
                    </span>
                  )}
                </div>
              </div>
              {auditReport.items && auditReport.items.filter((it) => it.status === "fixed" || it.status === "fix_failed" || it.error).length === 0 ? (
                <div className="text-sm text-slate-500 py-3 text-center">
                  ✨ Nenhuma inconsistência encontrada — todas as traduções estão corretas.
                </div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto text-sm">
                  {auditReport.items
                    .filter((it) => it.status === "fixed" || it.status === "fix_failed" || it.error)
                    .map((it, i) => (
                      <div key={`audit-${it.key || i}-${i}`} className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
                        {it.status === "fixed" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1">
                          <div className="text-xs text-slate-500 mb-0.5">
                            {ENTITY_LABELS[it.entity] || it.entity} · {LANG_META[it.lang]?.flag} {it.lang?.toUpperCase()}
                          </div>
                          <div className="text-slate-500">
                            <span className="italic">{it.source}</span>
                            <span className="mx-2 text-slate-400">PT</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="line-through text-rose-600/70">{it.current}</span>
                            <span className="text-slate-400">→</span>
                            <span className="font-semibold text-emerald-700">{it.corrected || it.error}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {lastReport && lastReport.items && lastReport.items.length > 0 && (
            <div className="mt-6 rounded-2xl bg-white shadow-sm border border-slate-200 p-5" data-testid="i18n-last-report">
              <div className="font-bold mb-3 text-slate-800">
                Último lote — {ENTITY_LABELS[lastReport.entityType] || lastReport.entityType} → {lastReport.lang.toUpperCase()}
              </div>
              <div className="space-y-1 max-h-80 overflow-y-auto text-sm">
                {lastReport.items.map((it, i) => (
                  <div key={`last-${it.key || i}-${i}`} className="flex items-start gap-2 py-1">
                    {it.saved ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className="text-slate-500">{it.source}</span>
                      <span className="mx-2 text-slate-400">→</span>
                      <span className="font-semibold text-slate-800">{it.translated || it.error}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
